import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  FileCheck, Plus, Upload, Download, Loader2, FileText, Building2, Trash2,
} from 'lucide-react';
import { pdvApi, documentsApi } from '../lib/api';
import api from '../lib/api';

const CATEGORIES = [
  { value: 'dvr', label: 'DVR — Documento Valutazione Rischi' },
  { value: 'duvri', label: 'DUVRI — Doc. Unico Valutazione Rischi Interferenze' },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function DvrDuvriPage() {
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [pdvs, setPdvs] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    pdv_id: '',
    category: 'dvr',
    name: '',
    expiry_date: '',
    file_url: '',
    filename: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [pdvRes, dvrRes, duvriRes] = await Promise.all([
        pdvApi.getAll(),
        documentsApi.getAll({ category: 'dvr' }),
        documentsApi.getAll({ category: 'duvri' }),
      ]);
      setPdvs(pdvRes.data || []);
      setDocuments([...(dvrRes.data || []), ...(duvriRes.data || [])]);
    } catch (error) {
      toast.error('Errore nel caricamento dei documenti DVR/DUVRI');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = (presetCategory = 'dvr', presetPdvId = '') => {
    setForm({
      pdv_id: presetPdvId,
      category: presetCategory,
      name: '',
      expiry_date: '',
      file_url: '',
      filename: '',
    });
    setDialogOpen(true);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File troppo grande (max 25 MB)');
      return;
    }
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/upload/documents', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm((f) => ({
        ...f,
        file_url: res.data.url,
        filename: res.data.filename,
        name: f.name || res.data.filename.replace(/\.[^/.]+$/, ''),
      }));
      toast.success('File caricato. Compila gli altri campi e salva.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const save = async () => {
    if (!form.pdv_id) { toast.error('Seleziona una Unità Locale'); return; }
    if (!form.name) { toast.error('Inserisci un nome documento'); return; }
    if (!form.file_url) { toast.error('Carica il file PDF prima di salvare'); return; }
    try {
      await documentsApi.create({
        pdv_id: form.pdv_id,
        category: form.category,
        name: form.name,
        file_url: form.file_url,
        expiry_date: form.expiry_date || null,
        reminder_days: 30,
      });
      toast.success('Documento creato');
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore creazione documento');
    }
  };

  const downloadDoc = (doc) => {
    if (!doc.file_url) { toast.error('File non disponibile'); return; }
    // file_url è del tipo /api/files/documents/<uuid>.pdf → prepend backend URL
    const fullUrl = doc.file_url.startsWith('http') ? doc.file_url : `${BACKEND_URL}${doc.file_url}`;
    window.open(fullUrl, '_blank');
  };

  const deleteDoc = async (doc) => {
    if (!window.confirm(`Eliminare "${doc.name}"?`)) return;
    try {
      await documentsApi.delete(doc.id);
      toast.success('Eliminato');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const dvrDocs = documents.filter((d) => d.category === 'dvr');
  const duvriDocs = documents.filter((d) => d.category === 'duvri');

  return (
    <div className="space-y-6 p-4 lg:p-6" data-testid="dvr-duvri-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileCheck className="w-6 h-6 text-primary" />
            DVR / DUVRI
          </h1>
          <p className="text-muted-foreground">Documenti di Valutazione dei Rischi e Interferenze</p>
        </div>
        <Button onClick={() => openCreate('dvr')} data-testid="new-document-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuovo Documento
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* DVR per UL */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              DVR per Unità Locale
            </CardTitle>
            <CardDescription>Stato di copertura DVR per ogni UL</CardDescription>
          </CardHeader>
          <CardContent>
            {pdvs.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">Nessuna Unità Locale registrata</p>
            ) : (
              <div className="space-y-2">
                {pdvs.slice(0, 8).map((pdv) => {
                  const dvrDoc = dvrDocs.find((d) => d.pdv_id === pdv.id);
                  return (
                    <div key={pdv.id} className="flex items-center justify-between p-2 rounded border">
                      <div className="flex items-center gap-2 min-w-0">
                        <Building2 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        <span className="text-sm truncate" title={pdv.name}>{pdv.name}</span>
                      </div>
                      {dvrDoc ? (
                        <Button variant="ghost" size="sm" onClick={() => downloadDoc(dvrDoc)} title="Scarica DVR" data-testid={`download-dvr-${pdv.id}`}>
                          <Download className="w-4 h-4" />
                        </Button>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => openCreate('dvr', pdv.id)} data-testid={`upload-dvr-${pdv.id}`}>
                          <Upload className="w-4 h-4 mr-1" /> Carica
                        </Button>
                      )}
                    </div>
                  );
                })}
                {pdvs.length > 8 && (
                  <p className="text-sm text-muted-foreground text-center">+{pdvs.length - 8} altre UL</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* DUVRI */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-orange-500" />
              DUVRI
            </CardTitle>
            <CardDescription>Documenti per la gestione delle interferenze con ditte esterne</CardDescription>
          </CardHeader>
          <CardContent>
            {duvriDocs.length === 0 ? (
              <div className="text-center py-8">
                <FileCheck className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground mb-3">Nessun DUVRI caricato</p>
                <Button variant="outline" onClick={() => openCreate('duvri')} data-testid="new-duvri-btn">
                  <Plus className="w-4 h-4 mr-2" /> Nuovo DUVRI
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {duvriDocs.slice(0, 6).map((d) => (
                  <div key={d.id} className="flex items-center justify-between p-2 rounded border">
                    <span className="text-sm truncate" title={d.name}>{d.name}</span>
                    <Button variant="ghost" size="sm" onClick={() => downloadDoc(d)}>
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="w-full" onClick={() => openCreate('duvri')}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Carica altro DUVRI
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Elenco completo */}
      <Card>
        <CardHeader>
          <CardTitle>Documenti DVR/DUVRI caricati</CardTitle>
        </CardHeader>
        <CardContent>
          {documents.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4" />
              <p>Nessun documento DVR/DUVRI caricato. Clicca "Nuovo Documento" per iniziare.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {documents.map((doc) => {
                const pdv = pdvs.find((p) => p.id === doc.pdv_id);
                return (
                  <div key={doc.id} className="flex items-center justify-between p-3 rounded border hover:bg-gray-50" data-testid={`doc-row-${doc.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${doc.category === 'dvr' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                          {doc.category.toUpperCase()}
                        </span>
                        <p className="font-medium truncate">{doc.name}</p>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {pdv ? `UL: ${pdv.name} · ` : ''}
                        Caricato: {doc.created_at ? new Date(doc.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}
                        {doc.expiry_date ? ` · Scade: ${new Date(doc.expiry_date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}` : ''}
                      </p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => downloadDoc(doc)} title="Scarica">
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteDoc(doc)} title="Elimina">
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Upload */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle>Nuovo documento DVR/DUVRI</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Categoria *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Unità Locale *</Label>
              <Select value={form.pdv_id || '__none__'} onValueChange={(v) => setForm({ ...form, pdv_id: v === '__none__' ? '' : v })}>
                <SelectTrigger data-testid="pdv-select"><SelectValue placeholder="Seleziona UL..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">-- nessuna --</SelectItem>
                  {pdvs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nome documento *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Es. DVR Eurobon 2026" data-testid="doc-name-input" />
            </div>
            <div>
              <Label>Data scadenza (opzionale)</Label>
              <Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} />
            </div>
            <div>
              <Label>File PDF *</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.doc"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="select-file-btn">
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  {form.filename ? 'Cambia file' : 'Seleziona file'}
                </Button>
                {form.filename && (
                  <span className="text-sm text-gray-600 truncate">{form.filename}</span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">PDF, DOCX o DOC (max 25 MB)</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={save} disabled={uploading} data-testid="save-document-btn">
              <Plus className="w-4 h-4 mr-2" /> Salva documento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
